import { useEffect, useState } from "react";
import Artwork from "../../assets/images/artlogo1.webp";
import RetryImage from "../ui/RetryImage";
import { PRINT_EMOJI_FONT, forPreview, printableText } from "../../util/printableText";
import { COVER_PAGE, COVER_TITLE_COLOR, browserMeasure, coverGeometry, layoutCoverTitle } from "../../util/coverPage";

/**
 * The front cover exactly as it prints (util/coverPage.js, a port of the
 * print renderer): the drawing filling the page at print shape, the
 * brush-stroke logo band across the bottom, the title on it in Permanent
 * Marker. Used everywhere the app shows the cover, from the step 4 preview
 * down to 44px thumbnails (decided 2026-10-23), so what people see is what
 * goes to the print partner.
 *
 * `imageUrl`    the cover drawing (or, before it exists, the cover photo)
 * `placeholder` shown above the band when there's no image (drawing, failed, empty)
 * `title`       the book title (upper-cased like print; empty prints "COLORING BOOK")
 */
let fontReady = typeof document === "undefined" || !document.fonts ? true : document.fonts.check('600 16px "Permanent Marker"');

const g = coverGeometry();
// The band starts this far down the page; placeholders sit above it.
const BAND_TOP = `${(g.logo.y / COVER_PAGE.height) * 100}%`;

const CoverPreview = ({ imageUrl = null, placeholder = null, title, className = "", alt = "" }) => {
  const [, setLoaded] = useState(fontReady);
  useEffect(() => {
    if (fontReady || !document.fonts) return undefined;
    let cancelled = false;
    // Sizes and line breaks depend on the font's widths: lay out again once it has loaded.
    document.fonts.load('600 100px "Permanent Marker"').then(() => {
      fontReady = true;
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasEmoji = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(printableText(title));
  const [, setEmojiFontReady] = useState(false);
  useEffect(() => {
    if (!hasEmoji || !document.fonts) return undefined;
    let cancelled = false;
    // Same as above for the emoji font, which only downloads when a title needs it.
    document.fonts.load('600 100px "Print Emoji"', "\u{1F495}").then(
      () => {
        if (!cancelled) setEmojiFontReady(true);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [hasEmoji]);

  const layout = layoutCoverTitle(title, browserMeasure);

  return (
    <div className={`relative aspect-[2625/3450] w-full overflow-hidden bg-[#F1ECE5] ${className}`}>
      {imageUrl ? (
        <RetryImage src={imageUrl} alt={alt} className="absolute inset-0 h-full w-full object-cover" />
      ) : placeholder ? (
        <div className="absolute inset-x-0 top-0" style={{ height: BAND_TOP }}>
          {placeholder}
        </div>
      ) : null}
      <svg
        viewBox={`0 0 ${COVER_PAGE.width} ${COVER_PAGE.height}`}
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        {/* The print SVG asks for dominant-baseline="middle", but its renderer (librsvg) draws the
            text on its baseline at y, which is what prints; browsers honour "middle" and would draw
            the title about half a cap height lower. So no dominant-baseline here. */}
        <image href={Artwork} x={g.logo.x} y={g.logo.y} width={g.logo.width} height={g.logo.height} preserveAspectRatio="xMidYMid meet" />
        <text
          x="50%"
          y={layout.firstLineY}
          fontFamily={`'Permanent Marker', ${PRINT_EMOJI_FONT}, cursive, sans-serif`}
          fontSize={layout.fontSize}
          fontWeight="600"
          fill={COVER_TITLE_COLOR}
          stroke={COVER_TITLE_COLOR}
          strokeWidth={layout.strokeWidth}
          textAnchor="middle"
        >
          {layout.lines.map((line, i) => (
            <tspan key={i} x="50%" dy={i === 0 ? 0 : layout.lineHeight}>
              {forPreview(line)}
            </tspan>
          ))}
        </text>
      </svg>
    </div>
  );
};

export default CoverPreview;
